import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft } from 'lucide-react';
import { authApi } from '@/lib/api';

interface EmailVerificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  onSuccess: () => void;
}

type Step = 'request' | 'verify' | 'success';

const animationConfig = {
  backdrop: { bg: 'rgba(0, 0, 0, 0.04)', blur: '8px' },
  duration: 400,
};

export function EmailVerificationModal({
  open,
  onOpenChange,
  email,
  onSuccess,
}: EmailVerificationModalProps) {
  const [step, setStep] = useState<Step>('request');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [code, setCode] = useState(['', '', '', '', '']);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [phase, setPhase] = useState<'entering' | 'visible' | 'exiting' | 'hidden'>('hidden');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const config = animationConfig;

  useEffect(() => {
    if (open) {
      setPhase('entering');
      const timer = setTimeout(() => setPhase('visible'), 50);
      return () => clearTimeout(timer);
    } else {
      setPhase('hidden');
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStep('request');
      setLoading(false);
      setError('');
      setCode(['', '', '', '', '']);
      setResendCountdown(0);
    }
  }, [open]);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  // Poll for email verification (in case user clicks the link in another tab)
  useEffect(() => {
    if (!open || step !== 'verify' || loading) return;

    const interval = setInterval(async () => {
      const response = await authApi.getMe();
      if (response.data?.is_email_verified) {
        clearInterval(interval);
        setLoading(false);
        setStep('success');
        setTimeout(() => {
          onSuccess();
          setPhase('exiting');
          setTimeout(() => {
            onOpenChange(false);
          }, config.duration);
        }, 1800);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [open, step, loading, onSuccess, onOpenChange, config.duration]);

  // Keep focus on OTP inputs when on verify step
  useEffect(() => {
    if (step !== 'verify' || loading) return;

    const focusFirstEmpty = () => {
      const firstEmptyIndex = code.findIndex((d) => !d);
      const targetIndex = firstEmptyIndex === -1 ? 4 : firstEmptyIndex;
      inputRefs.current[targetIndex]?.focus();
    };

    // Initial focus
    focusFirstEmpty();

    // Refocus when clicking outside inputs
    const handleFocusOut = (e: FocusEvent) => {
      const isInputFocused = inputRefs.current.some((input) => input === e.relatedTarget);
      if (!isInputFocused) {
        // Small delay to allow paste events to complete
        setTimeout(focusFirstEmpty, 10);
      }
    };

    const container = inputRefs.current[0]?.parentElement;
    container?.addEventListener('focusout', handleFocusOut);

    return () => {
      container?.removeEventListener('focusout', handleFocusOut);
    };
  }, [step, code, loading]);

  const handleClose = useCallback(() => {
    // Don't allow backdrop dismiss on verify (use back arrow) or success (auto-closes)
    if (step === 'verify' || step === 'success') return;
    setPhase('exiting');
    setTimeout(() => {
      onOpenChange(false);
    }, config.duration);
  }, [onOpenChange, config.duration, step]);

  const handleSendCode = async () => {
    setLoading(true);
    setError('');

    const response = await authApi.requestEmailVerification();

    setLoading(false);

    // if rate limited, just go to verify step
    // so no ugly error and can enter code that's already in their inbox
    if (response.rateLimited) {
      setStep('verify');
      setResendCountdown(60); // and they can resend after
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
      return;
    }

    if (response.error) {
      setError(response.error);
      return;
    }

    setStep('verify');
    setResendCountdown(60);
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  };

  const handleVerifyCode = useCallback(async (codeValue: string[]) => {
    const fullCode = codeValue.join('');
    if (fullCode.length !== 5) return;

    setLoading(true);
    setError('');

    const response = await authApi.verifyEmail(fullCode);

    if (response.error) {
      setError(response.error);
      setLoading(false);
      return;
    }

    setLoading(false);
    setStep('success');

    setTimeout(() => {
      onSuccess();
      setPhase('exiting');
      setTimeout(() => {
        onOpenChange(false);
      }, config.duration);
    }, 1800);
  }, [onSuccess, onOpenChange, config.duration]);

  const handleCodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);

    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);

    if (error) setError('');

    if (digit && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }

    if (digit && newCode.every((d) => d)) {
      handleVerifyCode(newCode);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);

    if (pastedData.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < 5; i++) {
        newCode[i] = pastedData[i] || '';
      }
      setCode(newCode);

      const focusIndex = Math.min(pastedData.length, 4);
      inputRefs.current[focusIndex]?.focus();

      if (newCode.every((d) => d)) {
        handleVerifyCode(newCode);
      }
    }
  };

  const handleResend = async () => {
    if (resendCountdown > 0) return;

    setError('');
    const response = await authApi.requestEmailVerification();

    if (response.error) {
      setError(response.error);
      return;
    }

    setResendCountdown(60);
    setCode(['', '', '', '', '']);
    inputRefs.current[0]?.focus();
  };

  const handleBack = () => {
    setStep('request');
    setCode(['', '', '', '', '']);
    setError('');
  };

  const showContent = phase !== 'hidden';
  if (!showContent && phase === 'hidden') return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        onClick={handleClose}
        className="absolute inset-0"
        style={{
          backgroundColor: phase === 'visible' ? config.backdrop.bg : 'rgba(0, 0, 0, 0)',
          backdropFilter: phase === 'visible' ? `blur(${config.backdrop.blur})` : 'blur(0px)',
          WebkitBackdropFilter: phase === 'visible' ? `blur(${config.backdrop.blur})` : 'blur(0px)',
          transition: `background-color ${config.duration}ms ease-out, backdrop-filter ${config.duration}ms ease-out`,
        }}
      />

      {/* Modal */}
      <div
        className={`relative w-full ${step === 'request' ? 'max-w-3xl' : 'max-w-md'}`}
        style={{
          opacity: phase === 'visible' ? 1 : 0,
          transform: phase === 'visible' ? 'scale(1)' : 'scale(0.98)',
          transition: `opacity ${config.duration}ms ease-out, transform ${config.duration}ms ease-out`,
        }}
      >
        <div className="relative bg-white dark:bg-zinc-900 rounded-[32px] shadow-2xl overflow-hidden">
          {/* Back button */}
          {step === 'verify' && (
            <button
              onClick={handleBack}
              className="absolute top-4 left-4 z-10 h-8 w-8 rounded-full text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}

          {/* Request Step */}
          {step === 'request' && (
            <div className="grid md:grid-cols-2">
              {/* Left side - Gmail-style inbox illustration */}
              <div className="bg-zinc-50 dark:bg-zinc-800/50 p-6 md:p-8 flex items-center justify-center">
                <div className="w-full max-w-[280px] rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg overflow-hidden">
                  {/* Inbox header */}
                  <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-800 flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-red-400 to-red-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">Inbox</span>
                    <div className="ml-auto flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-zinc-300 dark:bg-zinc-600" />
                      <div className="w-2 h-2 rounded-full bg-zinc-300 dark:bg-zinc-600" />
                      <div className="w-2 h-2 rounded-full bg-zinc-300 dark:bg-zinc-600" />
                    </div>
                  </div>

                  {/* Email list */}
                  <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                    {/* Skeleton email 1 */}
                    <div className="px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-2.5 bg-zinc-200 dark:bg-zinc-700 rounded w-24" />
                        <div className="h-2 bg-zinc-100 dark:bg-zinc-800 rounded w-32" />
                      </div>
                      <div className="h-2 bg-zinc-100 dark:bg-zinc-800 rounded w-8" />
                    </div>

                    {/* Verification email - highlighted */}
                    <div className="px-4 py-3 flex items-center gap-3 bg-blue-50 dark:bg-blue-950/30">
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-[10px] font-bold tracking-tight pl-px">
                        IO
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">InvoiceOwl</p>
                        <p className="text-xs text-zinc-600 dark:text-zinc-400 truncate">Your verification code is...</p>
                      </div>
                      <span className="text-[10px] text-blue-600 dark:text-blue-400 font-medium whitespace-nowrap">now</span>
                    </div>

                    {/* Skeleton email 2 */}
                    <div className="px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-2.5 bg-zinc-200 dark:bg-zinc-700 rounded w-20" />
                        <div className="h-2 bg-zinc-100 dark:bg-zinc-800 rounded w-36" />
                      </div>
                      <div className="h-2 bg-zinc-100 dark:bg-zinc-800 rounded w-6" />
                    </div>

                    {/* Skeleton email 3 */}
                    <div className="px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-2.5 bg-zinc-200 dark:bg-zinc-700 rounded w-28" />
                        <div className="h-2 bg-zinc-100 dark:bg-zinc-800 rounded w-24" />
                      </div>
                      <div className="h-2 bg-zinc-100 dark:bg-zinc-800 rounded w-10" />
                    </div>

                    {/* Skeleton email 4 */}
                    <div className="px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-2.5 bg-zinc-200 dark:bg-zinc-700 rounded w-16" />
                        <div className="h-2 bg-zinc-100 dark:bg-zinc-800 rounded w-28" />
                      </div>
                      <div className="h-2 bg-zinc-100 dark:bg-zinc-800 rounded w-8" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Right side - Form */}
              <div className="px-8 py-8 md:py-10 flex flex-col justify-center">
                <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
                  Verify your email
                </h2>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6">
                  We'll send a 5-digit code to confirm your address before you can send invoices and quotes.
                </p>

                {/* Email display */}
                <div className="bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 mb-6">
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-0.5">Sending to</p>
                  <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{email}</p>
                </div>

                {/* Error */}
                {error && (
                  <p className="text-sm text-red-500 dark:text-red-400 mb-4">{error}</p>
                )}

                {/* CTA */}
                <Button
                  className="w-full h-11"
                  onClick={handleSendCode}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send verification code'
                  )}
                </Button>

                <button
                  onClick={handleClose}
                  className="w-full mt-3 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
                >
                  Maybe later
                </button>
              </div>
            </div>
          )}

          {/* Verify Step */}
          {step === 'verify' && (
            <div
              className="px-8 py-10 md:px-10"
              onClick={() => {
                // Refocus on click anywhere in the verify step
                const firstEmptyIndex = code.findIndex((d) => !d);
                const targetIndex = firstEmptyIndex === -1 ? 4 : firstEmptyIndex;
                inputRefs.current[targetIndex]?.focus();
              }}
            >
              <div className="text-center max-w-sm mx-auto">
                <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
                  Check your inbox
                </h2>
                <p className="text-zinc-500 dark:text-zinc-400 mb-8">
                  Enter the 5-digit code we sent to<br />
                  <span className="font-medium text-zinc-700 dark:text-zinc-300">{email}</span>
                </p>

                {/* OTP Inputs */}
                <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => { inputRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      autoFocus={index === 0}
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className={`w-12 h-14 text-center text-2xl font-semibold rounded-xl border-2 bg-zinc-50 dark:bg-zinc-800 focus:outline-none focus:ring-0 transition-colors ${
                        error
                          ? 'border-red-400 dark:border-red-500'
                          : 'border-zinc-200 dark:border-zinc-700 focus:border-blue-500 dark:focus:border-blue-400'
                      }`}
                      disabled={loading}
                    />
                  ))}
                </div>

                {/* Error */}
                {error && (
                  <p className="text-sm text-red-500 dark:text-red-400 mb-4">{error}</p>
                )}

                {/* Loading */}
                {loading && (
                  <div className="flex items-center justify-center gap-2 text-zinc-500 dark:text-zinc-400 mb-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Verifying...</span>
                  </div>
                )}

                {/* Resend */}
                <button
                  onClick={handleResend}
                  disabled={resendCountdown > 0}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:text-zinc-400 dark:disabled:text-zinc-500 disabled:cursor-not-allowed transition-colors"
                >
                  {resendCountdown > 0
                    ? `Resend code in ${resendCountdown}s`
                    : "Didn't receive it? Resend code"}
                </button>

                {/* Spam folder hint */}
                <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-4">
                  Don't see it? Check your spam folder.
                </p>
              </div>
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="px-8 py-10 md:px-10">
              <div className="text-center py-4">
                {/* Animated checkmark */}
                <div className="relative mx-auto w-16 h-16 mb-6">
                  <div className="absolute inset-0 rounded-full bg-emerald-500/20 dark:bg-emerald-400/20 animate-ping" style={{ animationDuration: '1.5s' }} />
                  <div className="relative w-full h-full rounded-full bg-emerald-500 dark:bg-emerald-400 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-white"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path
                        d="M5 13l4 4L19 7"
                        className="animate-[draw_0.3s_ease-out_0.2s_forwards]"
                        style={{
                          strokeDasharray: 24,
                          strokeDashoffset: 24,
                        }}
                      />
                    </svg>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
                  Email verified!
                </h2>
                <p className="text-zinc-500 dark:text-zinc-400">
                  You're all set to send invoices and quotes.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS for checkmark animation */}
      <style>{`
        @keyframes draw {
          to {
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </div>
  );
}
